/* BONAVENTURA TH: one free message card per cart/order. */
(() => {
  if (customElements.get('bv-gift-message')) return;

  const CARD = 'Gift message card';
  const MESSAGE = 'Gift message';
  const selector = 'bv-gift-message';
  const copy = {
    en: {
      title: 'Your gift message', subtitle: 'Add a personal touch to your gift.',
      one: 'One complimentary card per order.', label: 'Message',
      limit: 'Up to 100 characters and 4 lines.',
      print: 'Your message will be printed as entered.',
      save: 'Save message', cancel: 'Cancel', close: 'Close',
      characters: 'characters', lines: 'lines',
      required: 'Please enter your message.',
      tooLong: 'Please use no more than 100 characters and 4 lines.',
      failed: 'We could not confirm the change. Please try again.',
      saving: 'Saving…', saved: 'Message saved.', removed: 'Message card removed.'
    },
    th: {
      title: 'ข้อความบนการ์ดของคุณ', subtitle: 'เพิ่มความพิเศษให้ของขวัญด้วยข้อความจากคุณ',
      one: 'การ์ดฟรี 1 ใบต่อคำสั่งซื้อ', label: 'ข้อความ',
      limit: 'สูงสุด 100 ตัวอักษร และ 4 บรรทัด',
      print: 'ข้อความจะถูกพิมพ์ตามที่คุณกรอก',
      save: 'บันทึกข้อความ', cancel: 'ยกเลิก', close: 'ปิด',
      characters: 'ตัวอักษร', lines: 'บรรทัด',
      required: 'กรุณากรอกข้อความ',
      tooLong: 'กรุณากรอกข้อความไม่เกิน 100 ตัวอักษร และ 4 บรรทัด',
      failed: 'ไม่สามารถยืนยันการเปลี่ยนแปลงได้ กรุณาลองอีกครั้ง',
      saving: 'กำลังบันทึก…', saved: 'บันทึกข้อความแล้ว', removed: 'นำการ์ดข้อความออกแล้ว'
    }
  };

  const segmenter = typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter('th', { granularity: 'grapheme' }) : null;

  function normalise(value) {
    return String(value || '').replace(/\r\n?/g, '\n');
  }

  function measure(value) {
    const text = normalise(value);
    return {
      characters: segmenter ? Array.from(segmenter.segment(text)).length : Array.from(text).length,
      lines: text ? text.split('\n').length : 0
    };
  }

  function valid(value) {
    const text = normalise(value);
    const size = measure(text);
    return text.trim().length > 0 && size.characters <= 100 && size.lines <= 4;
  }

  function fromCart(cart) {
    const attributes = cart.attributes || {};
    return {
      requested: attributes[CARD] === 'Yes',
      message: normalise(attributes[MESSAGE])
    };
  }

  function matchesChange(cart, requested, message) {
    const attributes = cart.attributes || {};
    return requested
      ? attributes[CARD] === 'Yes' && normalise(attributes[MESSAGE]) === normalise(message)
      : !attributes[CARD] && !attributes[MESSAGE];
  }

  let state = null;
  let busy = false;
  let editor = null;
  let revision = 0;
  let notice = '';
  let refreshing = null;
  let pendingRequested = null;

  const widgets = () => document.querySelectorAll(selector);
  const language = widget => widget.dataset.locale === 'th' ? 'th' : 'en';
  const texts = widget => copy[language(widget)];
  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  function paint() {
    if (!state) return;
    widgets().forEach(widget => {
      const toggle = widget.querySelector('[data-bv-toggle]');
      const preview = widget.querySelector('[data-bv-preview]');
      const text = widget.querySelector('[data-bv-message]');
      const status = widget.querySelector('[data-bv-status]');
      if (!toggle || !preview || !text || !status) return;

      // Show selection immediately; cancellation restores the saved state.
      toggle.checked = pendingRequested ?? (state.requested || Boolean(editor));
      // Conditional writes prevent the redraw observer from looping.
      if (toggle.disabled !== busy) toggle.disabled = busy;
      preview.hidden = !state.requested;
      if (text.textContent !== state.message) text.textContent = state.message;
      widget.querySelectorAll('[data-bv-action]').forEach(button => {
        if (button.disabled !== busy) button.disabled = busy;
      });
      const label = notice ? texts(widget)[notice] : '';
      if (status.textContent !== label) status.textContent = label;
      widget.setAttribute('aria-busy', String(busy));
    });

    // Cart attributes use the normal checkout path.
    document.querySelectorAll('.additional-checkout-buttons').forEach(node => {
      if (state.requested && !node.hidden) {
        node.hidden = true;
        node.dataset.bvGiftHidden = 'true';
      } else if (!state.requested && node.dataset.bvGiftHidden === 'true') {
        node.hidden = false;
        delete node.dataset.bvGiftHidden;
      }
    });
  }

  async function request(endpoint, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(root() + endpoint, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  function refresh() {
    if (busy || !widgets().length) return Promise.resolve();
    if (refreshing) return refreshing;
    const stamp = revision;
    refreshing = request('cart.js')
      .then(cart => {
        if (!busy && revision === stamp) {
          state = fromCart(cart);
          paint();
        }
      })
      .catch(() => {})
      .finally(() => { refreshing = null; });
    return refreshing;
  }

  async function commit(requested, message) {
    if (busy || (requested && !valid(message))) return false;
    message = requested ? normalise(message) : '';
    busy = true;
    pendingRequested = requested;
    revision += 1;
    notice = 'saving';
    paint();
    if (editor) editor.update();

    try {
      let cart = await request('cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          attributes: { [CARD]: requested ? 'Yes' : '', [MESSAGE]: message }
        })
      });
      if (!matchesChange(cart, requested, message)) cart = await request('cart.js');
      if (!matchesChange(cart, requested, message)) throw new Error('Save not confirmed');
      state = fromCart(cart);
      notice = requested ? 'saved' : 'removed';
      return true;
    } catch (_) {
      notice = 'failed';
      if (editor) editor.error.textContent = editor.t.failed;
      return false;
    } finally {
      busy = false;
      pendingRequested = null;
      paint();
      if (editor) editor.update();
    }
  }

  function openEditor(widget, opener) {
    if (busy || editor || !state) return;
    notice = '';
    const t = texts(widget);
    const dialog = document.createElement('dialog');
    dialog.className = 'bv-gift-dialog notranslate';
    dialog.lang = language(widget);
    dialog.setAttribute('aria-labelledby', 'bv-gift-title');
    dialog.innerHTML =
      '<button type="button" class="bv-gift-close" data-close>×</button>' +
      '<h2 id="bv-gift-title" data-title></h2>' +
      '<p class="bv-gift-subtitle" data-subtitle></p>' +
      '<p class="bv-gift-muted" data-one></p>' +
      '<form data-form novalidate>' +
        '<label for="bv-gift-input" data-label></label>' +
        '<textarea id="bv-gift-input" rows="4" autocomplete="off" translate="no" ' +
          'aria-describedby="bv-gift-limit bv-gift-counter bv-gift-error"></textarea>' +
        '<p id="bv-gift-limit" class="bv-gift-muted" data-limit></p>' +
        '<div id="bv-gift-counter" class="bv-gift-counter"><span data-count></span><span data-lines></span></div>' +
        '<p id="bv-gift-error" class="bv-gift-error" role="alert"></p>' +
        '<p class="bv-gift-muted" data-print></p>' +
        '<button type="submit" class="bv-gift-save" data-save></button>' +
        '<button type="button" class="bv-gift-cancel" data-cancel></button>' +
      '</form>';

    ['title', 'subtitle', 'one', 'label', 'limit', 'print', 'save', 'cancel'].forEach(key => {
      dialog.querySelector('[data-' + key + ']').textContent = t[key];
    });
    dialog.querySelector('[data-close]').setAttribute('aria-label', t.close);
    const field = dialog.querySelector('textarea');
    const save = dialog.querySelector('[data-save]');
    const error = dialog.querySelector('#bv-gift-error');
    const cancel = dialog.querySelector('[data-cancel]');
    const close = dialog.querySelector('[data-close]');
    field.value = state.requested ? state.message : '';
    let composing = false;

    function update(showErrors = false) {
      const size = measure(field.value);
      dialog.querySelector('[data-count]').textContent = size.characters + ' / 100 ' + t.characters;
      dialog.querySelector('[data-lines]').textContent = size.lines + ' / 4 ' + t.lines;
      save.disabled = busy || composing || !valid(field.value);
      field.readOnly = busy;
      cancel.disabled = close.disabled = busy;
      save.textContent = busy ? t.saving : t.save;
      if (showErrors) {
        error.textContent = !field.value.trim() ? t.required
          : !valid(field.value) ? t.tooLong : '';
      }
      field.setAttribute('aria-invalid', String(!valid(field.value) && !!field.value));
    }

    editor = { dialog, update, error, t };
    field.addEventListener('input', () => update(true));
    field.addEventListener('compositionstart', () => { composing = true; update(); });
    field.addEventListener('compositionend', () => { composing = false; update(true); });
    dialog.querySelector('[data-form]').addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || composing || !valid(field.value)) {
        update(true);
        return;
      }
      error.textContent = '';
      if (await commit(true, field.value)) dialog.close();
    });

    const dismiss = () => { if (!busy) dialog.close(); };
    cancel.addEventListener('click', dismiss);
    close.addEventListener('click', dismiss);
    dialog.addEventListener('cancel', event => { if (busy) event.preventDefault(); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom) dismiss();
    });
    dialog.addEventListener('close', () => {
      editor = null;
      dialog.remove();
      paint();
      if (opener?.isConnected) opener.focus();
      else widget.querySelector('[data-bv-toggle]')?.focus();
    });

    document.body.appendChild(dialog);
    update();
    dialog.showModal();
    field.focus();
    paint();
  }

  class GiftMessage extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        if (!state) {
          state = {
            requested: this.dataset.card === 'Yes',
            message: normalise(this.dataset.message)
          };
        }
        paint();
        refresh();
      });
    }
  }
  customElements.define('bv-gift-message', GiftMessage);

  // Restore controls when the theme morphs/reuses cart markup.
  let giftPaintFrame = 0;
  const giftObserver = new MutationObserver(records => {
    const affected = records.some(record => {
      const target = record.target instanceof Element
        ? record.target : record.target.parentElement;
      if (target?.closest(selector)) return true;
      return Array.from(record.addedNodes || []).some(node =>
        node instanceof Element && (node.matches(selector) || node.querySelector(selector))
      );
    });
    if (!affected || giftPaintFrame) return;
    giftPaintFrame = requestAnimationFrame(() => {
      giftPaintFrame = 0;
      paint();
    });
  });
  giftObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled']
  });

  document.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof Element) || !input.matches('[data-bv-toggle]')) return;
    const widget = input.closest(selector);
    if (!widget) return;
    const requested = input.checked;
    paint();
    if (requested) openEditor(widget, input);
    else commit(false, '');
  });

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-bv-action]');
    const widget = button?.closest(selector);
    if (!widget) return;
    event.preventDefault();
    if (button.dataset.bvAction === 'edit') openEditor(widget, button);
    else commit(false, '');
  });

  function guardCheckout(event) {
    if (!state || !widgets().length) return;
    let checkout = false;
    if (event.type === 'submit') {
      checkout = event.submitter?.name === 'checkout' ||
        event.target.getAttribute('action')?.includes('/checkout');
    } else {
      checkout = event.composedPath().some(node => node instanceof Element &&
        node.matches('[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons, shopify-accelerated-checkout, shopify-accelerated-checkout-cart'));
    }
    if (!checkout) return;
    if (busy || editor || (state.requested && !valid(state.message))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const widget = widgets()[0];
      if (!busy && !editor) openEditor(widget, widget.querySelector('[data-bv-toggle]'));
      else if (!editor) {
        notice = 'saving';
        paint();
      }
    }
  }

  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  document.addEventListener('cart:update', () => refresh());
  window.addEventListener('pageshow', () => refresh());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh();
  });
})();
